import React, { lazy, Suspense, useEffect } from "react";
import "../src/style.scss";
import "../src/unhealthieStyle.scss";
import "@aws-amplify/ui-react/styles.css";

import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import Default from "./appointment/Default";
import PageLoadingSpinner from "./common/unhealthie/PageLoadingSpinner";
import { preventDefaults } from "./helpers";
import ResearchChat from "./provider-portal/nutrition-research/ResearchChat";
import { FayStatsigProvider } from "./common/unhealthie/hooks/useFayStatsig";
import { configureAuthenticator } from "./common/auth/cognitoConfig";
import { Authenticator } from "@aws-amplify/ui-react";

configureAuthenticator();

const PatientDashboard = lazy(
  () => import("./patient-dashboard/PatientDashboard"),
);

// Booking/Intake Flow
const BookingApp = lazy(() => import("./BookingApp"));
const IntakePageWrapper = lazy(() => import("./intake/IntakePageWrapper"));

// Pricing Page (iFrame embed)
const PricePage = lazy(() => import("./price-estimator/PricePage"));

// New Provider Portal
const ProviderPortal = lazy(
  () => import("./provider-portal/ProviderPortalContainer"),
);
const ScorecardPage = lazy(
  () => import("./provider-portal/scorecard/ScorecardPage"),
);
const AdminPage = lazy(() => import("./provider-portal/admin/AdminPage"));
const ProviderCalendar = lazy(
  () => import("./provider-portal/calendar/CalendarPage"),
);
const Chat = lazy(() => import("./common/unhealthie/chat/ChatPage"));
const NoConversation = lazy(() =>
  import("./common/unhealthie/chat/Conversation").then((module) => ({
    default: module.NoConversation,
  })),
);
const ClientProviderChat = lazy(
  () => import("./common/unhealthie/chat/ClientProviderChat"),
);
const ProviderSettingsPage = lazy(
  () => import("./provider-portal/settings/ProviderSettingsPage"),
);
const ClientsOverviewPage = lazy(
  () => import("./provider-portal/clients/ClientsOverviewPage"),
);

const ProviderClientProfilePage = lazy(() =>
  import("./provider-portal/clients/ClientProfilePage").then((module) => ({
    default: module.ProviderClientProfilePage,
  })),
);

const PayoutsPage = lazy(() => import("./provider-portal/payouts/PayoutsPage"));

const ProviderProfileMenu = lazy(
  () => import("./provider-portal/settings/ProviderProfileMenu"),
);

const ProviderIntegrationsMenu = lazy(
  () => import("./provider-portal/settings/ProviderIntegrationsMenu"),
);

const ProviderAccountMenu = lazy(
  () => import("./provider-portal/settings/ProviderAccountMenu"),
);

const OAuthRedirectHandler = lazy(
  () => import("./provider-portal/OAuthRedirectHandler"),
);

const JoinMeetingLandingPage = lazy(
  () => import("./provider-portal/JoinMeetingLandingPage"),
);

const MealPlanPage = lazy(
  () => import("./provider-portal/meal-planning/MealPlanPage"),
);
const MealPlan = lazy(() => import("./provider-portal/meal-planning/MealPlan"));
const EditMealRecipe = lazy(
  () => import("./provider-portal/meal-planning/EditMealRecipe"),
);

const OnboardingFlow = lazy(
  () => import("./provider-portal/onboarding/OnboardingFlow"),
);

const MarketingPage = lazy(
  () => import("./provider-portal/marketing/MarketingPage"),
);

const ProviderReferralLandingPage = lazy(
  () => import("./provider-portal/ProviderReferralLandingPage"),
);

const ProviderReviewLinkLandingPage = lazy(
  () => import("./provider-portal/ProviderReviewLinkLandingPage"),
);

const ProviderClientCare = lazy(
  () => import("./provider-portal/settings/my-client-care/ProviderClientCare"),
);

const ProviderApp = () => {
  // Prevent file drags from opening the file in the browser and navigating
  // away from the app.
  useEffect(() => {
    window.addEventListener("dragover", preventDefaults, false);
    window.addEventListener("drop", preventDefaults, false);

    return () => {
      window.removeEventListener("dragover", preventDefaults);
      window.removeEventListener("drop", preventDefaults);
    };
  }, []);

  return (
    <FayStatsigProvider>
      <Authenticator.Provider>
        <Suspense fallback={<PageLoadingSpinner />}>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Default />} />
              <Route
                path="/onboarding/"
                element={<Navigate to="/onboarding/welcome" />}
              />
              <Route
                path="/patient-dashboard/"
                element={<PatientDashboard />}
              />
              <Route
                path="/onboarding/:multiStepFormPath"
                element={<OnboardingFlow />}
              />
              <Route path="/appointments" element={<BookingApp />} />
              <Route path="/appointments/embed_appt" element={<BookingApp />} />
              <Route
                path="/zocdoc-intake/:bookingCode"
                element={<IntakePageWrapper />}
              />
              <Route path="/pricing" element={<PricePage />} />
              <Route
                // Redirect legacy provider portal URL to new one
                path="/provider-portal"
                element={<Navigate to={`/provider`} />}
              />
              <Route path="/provider" element={<ProviderPortal />}>
                <Route
                  index
                  element={<Navigate to={`/provider/dashboard`} />}
                />
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <ScorecardPage />
                    </Suspense>
                  }
                />
                <Route
                  path="research"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <ResearchChat />
                    </Suspense>
                  }
                />
                <Route
                  path="appointments/:appointmentId/:action?"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <JoinMeetingLandingPage />
                    </Suspense>
                  }
                ></Route>
                <Route
                  path="calendar/:calendarEventId?/:action?"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <ProviderCalendar />
                    </Suspense>
                  }
                />
                <Route
                  path="clients"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <ClientsOverviewPage />
                    </Suspense>
                  }
                />
                <Route
                  path="clients/:patientId/:tab?"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <ProviderClientProfilePage />
                    </Suspense>
                  }
                />
                <Route
                  path="chat"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <Chat />
                    </Suspense>
                  }
                >
                  <Route
                    index
                    element={
                      <Suspense fallback={<PageLoadingSpinner />}>
                        <NoConversation />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":respondentId"
                    element={
                      <Suspense fallback={<PageLoadingSpinner />}>
                        <ClientProviderChat />
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path="payouts"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <PayoutsPage />
                    </Suspense>
                  }
                />

                <Route
                  path="meal-planner"
                  element={<Navigate to={"/provider/meal-plan"} />}
                />
                <Route
                  path="meal-plan"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <MealPlanPage />
                    </Suspense>
                  }
                >
                  <Route
                    path=":mealPlanId"
                    element={
                      <Suspense fallback={<PageLoadingSpinner />}>
                        <MealPlan />
                      </Suspense>
                    }
                  >
                    <Route
                      path="edit/:mealTypeOrId"
                      element={
                        <Suspense fallback={<PageLoadingSpinner />}>
                          <EditMealRecipe />
                        </Suspense>
                      }
                    />
                  </Route>
                </Route>

                <Route path="marketing" element={<MarketingPage />} />
                <Route
                  path="admin/:toolSlug?"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <AdminPage />
                    </Suspense>
                  }
                />

                <Route
                  path="settings"
                  element={
                    <Suspense fallback={<PageLoadingSpinner />}>
                      <ProviderSettingsPage />
                    </Suspense>
                  }
                >
                  <Route
                    index
                    element={<Navigate to={`/provider/settings/profile`} />}
                  />
                  <Route
                    path="profile"
                    element={
                      <Suspense fallback={<PageLoadingSpinner />}>
                        <ProviderProfileMenu />
                      </Suspense>
                    }
                  />
                  <Route
                    path="account"
                    element={
                      <Suspense fallback={<PageLoadingSpinner />}>
                        <ProviderAccountMenu />
                      </Suspense>
                    }
                  />
                  <Route
                    path="integrations"
                    element={
                      <Suspense fallback={<PageLoadingSpinner />}>
                        <ProviderIntegrationsMenu />
                      </Suspense>
                    }
                  />
                  <Route
                    path="my-client-care"
                    element={
                      <Suspense fallback={<PageLoadingSpinner />}>
                        <ProviderClientCare />
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path="oauth/:oauthProvider"
                  element={<OAuthRedirectHandler />}
                />
              </Route>

              <Route
                path="/book/:providerSlug/:privateBookingCode"
                element={<ProviderReferralLandingPage />}
              />
              <Route
                path="/review/:providerSlug"
                element={<ProviderReviewLinkLandingPage />}
              />
            </Routes>
          </BrowserRouter>
        </Suspense>
      </Authenticator.Provider>
    </FayStatsigProvider>
  );
};

export default ProviderApp;
